import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { mockProjects } from '../data/mock';

export default function ProjectsView() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState(mockProjects);
  const [formData, setFormData] = useState({ name: '', category: '', budget: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewProject = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate network delay
    setTimeout(() => {
      const newProj = {
        id: `p${Date.now()}`,
        name: formData.name,
        status: 'design',
        progress: 0,
        dueDate: 'TBD',
        category: formData.category || 'Misc',
        budget: parseInt(formData.budget) || 0,
        spent: 0,
        tags: ['New Build'],
      };
      setProjects([newProj, ...projects]);
      setIsSubmitting(false);
      setIsModalOpen(false);
      setFormData({ name: '', category: '', budget: '' });
    }, 1500);
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <header className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Project Tracker</h1>
          <p className="text-gray-400 font-mono text-sm">MANAGE // EXECUTE</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-maker-cyan text-graphite-900 px-4 py-2 rounded-lg font-bold hover:bg-white transition-colors glow-cyan"
        >
          <SafeIcon name="Plus" />
          <span className="hidden sm:inline">Initialize Build</span>
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
        {projects.map((project) => (
          <motion.div 
            key={project.id}
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-panel rounded-xl overflow-hidden flex flex-col group"
          >
            {/* Header Area representing a Blueprint/Photo placeholder */}
            <div className="h-32 bg-graphite-700 relative overflow-hidden flex items-center justify-center border-b border-graphite-600">
               <div className="absolute inset-0 bg-grid-pattern opacity-30" />
               <SafeIcon name="Image" className="w-10 h-10 text-graphite-500 z-10" />
               <div className="absolute top-2 right-2 px-2 py-1 text-[10px] font-mono rounded bg-graphite-900/80 text-gray-300 border border-graphite-600 z-10">
                 {project.category}
               </div>
            </div>
            
            <div className="p-5 flex-1 flex flex-col">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-maker-cyan transition-colors">{project.name}</h3>
              
              {/* Status & Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-xs font-mono text-gray-400 mb-1 uppercase">
                  <span>Status: {project.status}</span>
                  <span className="text-maker-cyan">{project.progress}%</span>
                </div>
                <div className="w-full bg-graphite-900 h-2 rounded-full overflow-hidden border border-graphite-700">
                  <div 
                    className="h-full bg-maker-cyan glow-cyan transition-all duration-1000"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tags.map(tag => (
                  <span key={tag} className="px-2 py-1 text-xs rounded-md bg-graphite-700/50 text-gray-300 border border-graphite-600">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-auto pt-4 border-t border-graphite-700 flex justify-between items-center text-sm">
                <div className="flex items-center gap-1 text-maker-tan">
                  <SafeIcon name="DollarSign" className="w-4 h-4" />
                  <span>{project.spent} / {project.budget}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <SafeIcon name="Calendar" className="w-4 h-4" />
                  <span className="font-mono text-xs">{project.dueDate}</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* New Project Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="glass-panel w-full max-w-md rounded-2xl overflow-hidden border-maker-cyan/30"
            >
              <div className="p-6 border-b border-graphite-700 bg-graphite-800 flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <SafeIcon name="Terminal" className="text-maker-cyan" />
                  Initialize New Build
                </h2>
                <button onClick={() => !isSubmitting && setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                  <SafeIcon name="X" />
                </button>
              </div>
              
              <div className="p-6">
                {isSubmitting ? (
                  <div className="flex flex-col items-center justify-center py-10 space-y-4">
                    <div className="w-12 h-12 border-4 border-graphite-700 border-t-maker-cyan rounded-full animate-spin glow-cyan" />
                    <p className="font-mono text-maker-cyan animate-pulse">ALLOCATING_RESOURCES...</p>
                  </div>
                ) : (
                  <form onSubmit={handleNewProject} className="space-y-4">
                    <div>
                      <label className="block text-xs font-mono text-gray-400 mb-1">PROJECT_DESIGNATION</label>
                      <input 
                        required
                        type="text" 
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-graphite-900 border border-graphite-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-maker-cyan focus:ring-1 focus:ring-maker-cyan transition-all"
                        placeholder="e.g. Iron Man Gauntlet"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-mono text-gray-400 mb-1">CATEGORY</label>
                        <select 
                          value={formData.category}
                          onChange={e => setFormData({...formData, category: e.target.value})}
                          className="w-full bg-graphite-900 border border-graphite-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-maker-cyan appearance-none"
                        >
                          <option value="">Select...</option>
                          <option value="Cosplay">Cosplay</option>
                          <option value="Woodworking">Woodworking</option>
                          <option value="Electronics">Electronics</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-mono text-gray-400 mb-1">EST_BUDGET ($)</label>
                        <input 
                          type="number" 
                          value={formData.budget}
                          onChange={e => setFormData({...formData, budget: e.target.value})}
                          className="w-full bg-graphite-900 border border-graphite-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-maker-cyan transition-all font-mono"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="mt-8 flex gap-3">
                      <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 rounded-lg border border-graphite-600 text-gray-300 hover:bg-graphite-700 transition-colors">
                        ABORT
                      </button>
                      <button type="submit" className="flex-1 py-2 rounded-lg bg-maker-cyan/20 border border-maker-cyan text-maker-cyan font-bold hover:bg-maker-cyan hover:text-graphite-900 transition-all glow-cyan">
                        EXECUTE
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}